import { MfsCollectionCommon, MfsCommon, MfsBase, MfsRequired } from './store/mfsTypes';

/** Well-known keys of MfsItems. */
export const COLLECTION_KIND_KEY = nameof<MfsCollectionCommon>('kind');
export const SECONDARY_LABELS_KEY = nameof<MfsCommon>('secondaryLabels');
export const DISPLAY_TEXT_KEY = nameof<MfsBase>('displayText');
export const ID_KEY = nameof<MfsBase>('id');
export const LABEL_PROPERTY = nameof<MfsRequired>('label');
export const LABEL_ID_PROPERTY = nameof<MfsCommon>('labelId');

/** Mapped type which removes the readonliness of top level properties of T. */
export type Writable<T> = { -readonly [P in keyof T]: T[P] };

/** Mapped type which makes selected properties required. */
type WithRequiredProps<T, K extends keyof T> = {
  [P in K]-?: T[K];
};

/** Mapped type which creates a filter out of a given type by turning all its attributes to optional match functions */
export type Filter<T> = {
  [Property in keyof T as `match${Capitalize<string & Property>}`]?: (Property: T[Property]) => boolean;
};

/** Checks if the source item has the given property with a value other than undefined. */
export function hasPropertyDefined<T extends Record<string, unknown>, K extends keyof T>(
  source: T,
  property: K
): source is T & WithRequiredProps<T, K> {
  return hasProperty(source, property) && source[property] !== undefined;
}

/** Returns the name of `key`. */
export function nameof<T>(key: keyof T): keyof T {
  return key;
}

/** Specifies whether the `input` is a `string`. */
export function isString(input: unknown): input is string {
  return typeof input === 'string' || input instanceof String;
}

/** Specifies whether `input` has the given `property`. */
export function hasProperty<T>(input: T, property: keyof T): boolean {
  return Object.prototype.hasOwnProperty.call(input, property);
}

/** Specifies whether the two given value types are deep equal. */
export function areEqual(x: unknown, y: unknown): boolean {
  if (x && y && typeof x === 'object' && typeof x === typeof y) {
    const objX = x as Record<string, unknown>;
    const objY = y as Record<string, unknown>;

    return (
      Object.keys(objX).length === Object.keys(objY).length &&
      Object.keys(objX).every((key) => areEqual(objX[key], objY[key]))
    );
  } else {
    return x === y;
  }
}

/** Whether the two arrays have at least one (deep) equal element. */
export function areIntersecting(x: unknown[], y: unknown[]): boolean {
  return x.filter((v) => y.some((p) => areEqual(v, p))).some((q) => q !== undefined && q !== null);
}

/** Specifies whether `str` is null, whitespace or empty string. */
export function isNullOrWhitespace(str: string): boolean {
  return !str || !str.trim();
}

/** Speficies whether `a` has any elements that is other than `undefined`. */
export function hasAnyElement(a: readonly unknown[]): boolean {
  return a.some((v) => v !== undefined);
}

/** Returns the elements of the given array that are not repeated. */
export function removeDuplicates<T>(source: readonly T[], areEqual?: (lhs: T, rhs: T) => boolean): T[] {
  const predicate =
    areEqual !== undefined
      ? (val: T, index: number, array: readonly T[]) => array.findIndex((v) => areEqual(val, v)) === index
      : (val: T, index: number, array: readonly T[]) => array.indexOf(val) === index;

  return source.filter((value, index, array) => predicate(value, index, array));
}

/** Upper-cases the first char of input string */
export function capitalize(input: string): string {
  return `${input.charAt(0).toUpperCase()}${input.substring(1)}`;
}

/** Whether `x` is a positive integer value */
export function isPositiveInteger(x: number): boolean {
  return Number.isInteger(x) && x > 0;
}

/** Gets the elements of the `source` array that are also elements of the `destination` array. */
export function intersect<T, U>(source: T[], destination: U[], mapFn: (v: U) => T): U[] {
  const vals = source.reduce((map, value) => map.set(value, true), new Map<T, boolean>([]));
  return destination.filter((v) => vals.get(mapFn(v)));
}

/** Gets the elements of the `source` array except the ones that are also elements of the `destination` array. */
export function except<T, U>(source: T[], destination: U[], mapFn: (v: U) => T): T[] {
  const vals = destination.reduce((map, value) => map.set(mapFn(value), true), new Map<T, boolean>([]));
  return source.filter((v) => !vals.get(v));
}
